/**
 * Workflow Inspector API Client
 *
 * Read-only API for inspecting completed workflow executions,
 * decision frames, and standards compliance.
 *
 * Follows: .equilateral-standards/serverless-saas-aws/api_standards.md
 */

import { Make_Authorized_API_Call, isSuccessResponse, type TimComboAPIResponse } from './apiClient';
import { API_ENDPOINTS } from '../services/api/endpoints';

// Re-export for consumers
export type { TimComboAPIResponse };

// ─── Types ───────────────────────────────────────────────────

export interface WorkflowExecutionSummary {
  Execution_ID: string;
  Workflow_ID: string;
  Workflow_Name: string;
  Status: 'pending' | 'running' | 'completed' | 'failed';
  Started_At: string;
  Completed_At: string | null;
  Duration_Ms: number | null;
  Total_Steps: number;
  Completed_Steps: number;
  Failed_Steps: number;
  Environment: string;
  Triggered_By: string;
  Error_Message: string | null;
}

export interface WorkflowStep {
  Step_ID: string;
  Step_Name: string;
  Agent_ID: string;
  Step_Order: number;
  Status: 'pending' | 'running' | 'completed' | 'failed';
  Started_At: string | null;
  Completed_At: string | null;
  Duration_Ms: number | null;
  Error_Details: string | null;
  Retry_Count: number;
}

export interface DecisionFrame {
  Frame_ID: string;
  Step_Name: string;
  Selected_Agent: string;
  Selection_Reason: string;
  Confidence: number | null;
  Confidence_Level: 'HIGH' | 'MEDIUM' | 'LOW' | 'UNKNOWN';
  Fallbacks: Array<{ agent: string; score: number; reason?: string }>;
  Score_Breakdown: Record<string, number>;
  Constraints_Checked: Array<{ invariant: string; tier: string; checked?: boolean }>;
  Constraints_Violated: Array<{ invariant: string; tier: string; reason: string; context?: string }>;
  Evidence_Gaps: Array<{ field: string; reason: string } | string>;
  Routed_By: string;
  Decided_At: string;
  Failover_Attempted: boolean;
  Failover_Strategy: string | null;
  Failover_History: Array<{
    originalAgent: string;
    fallbackAgent: string;
    strategy: string;
    reason: string;
    success: boolean;
    timestamp: string;
  }>;
  Outcome: 'PENDING' | 'SUCCESS' | 'FAILURE' | 'FAILOVER_SUCCESS';
  Error_Message: string | null;
  Duration_Ms: number | null;
}

export interface ComplianceRecord {
  ID: number;
  Standard_ID: number;
  Standard_Title: string;
  Standard_Category: string | null;
  Standard_Priority: number | null;
  Relevance_Score: number | null;
  Followed: boolean | null;
  Violated: boolean | null;
  Violation_Details: {
    reason?: string;
    detected_by?: string;
    severity?: string;
    context?: string;
  } | null;
  Step_Name: string | null;
  Profile_ID: string | null;
}

export interface ComplianceSummary {
  Total_Delivered: number;
  Followed: number;
  Violated: number;
  Compliance_Rate: number;
}

export interface AuditEntry {
  Audit_ID: number;
  Event_Type: string;
  Entity_Type: string;
  Entity_ID: string;
  Outcome: string;
  Details: any;
  Created_At: string;
  Error_Message: string | null;
}

export interface ExecutionDetail {
  Execution: WorkflowExecutionSummary;
  Steps: WorkflowStep[];
  Decision_Frames: DecisionFrame[];
  Compliance: {
    Standards: ComplianceRecord[];
    Summary: ComplianceSummary;
  };
  Audit_Trail: AuditEntry[];
}

// ─── API Functions ───────────────────────────────────────────

export interface ListExecutionsParams {
  limit?: number;
  offset?: number;
  status?: 'pending' | 'running' | 'completed' | 'failed';
  startDate?: string;
  endDate?: string;
}

/**
 * List workflow executions with pagination and filtering
 * GET /api/workflow-inspector/executionsListGet
 */
export async function listWorkflowExecutions(
  params: ListExecutionsParams = {},
): Promise<TimComboAPIResponse<WorkflowExecutionSummary>> {
  const queryParams: Record<string, any> = {};
  if (params.limit) queryParams.limit = params.limit;
  if (params.offset) queryParams.offset = params.offset;
  if (params.status) queryParams.status = params.status;
  if (params.startDate) queryParams.startDate = params.startDate;
  if (params.endDate) queryParams.endDate = params.endDate;

  return Make_Authorized_API_Call<WorkflowExecutionSummary>(
    API_ENDPOINTS.WORKFLOW_INSPECTOR.EXECUTIONS_LIST,
    'GET',
    undefined,
    { params: queryParams }
  );
}

/**
 * Get full execution detail including steps, decision frames, compliance, audit trail
 * GET /api/workflow-inspector/executionDetailGet
 */
export async function getExecutionDetail(
  executionId: string,
): Promise<TimComboAPIResponse<ExecutionDetail>> {
  return Make_Authorized_API_Call<ExecutionDetail>(
    API_ENDPOINTS.WORKFLOW_INSPECTOR.EXECUTION_DETAIL,
    'GET',
    undefined,
    { params: { execution_id: executionId } }
  );
}

// ─── Formatting Utilities ────────────────────────────────────

export function formatDuration(ms: number | null): string {
  if (ms === null || ms === undefined) return '-';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.round((ms % 60000) / 1000);
  return `${minutes}m ${seconds}s`;
}

export function getStatusColor(
  status: string,
): 'success' | 'failure' | 'info' | 'warning' | 'gray' {
  switch (status) {
    case 'completed':
    case 'SUCCESS':
    case 'FAILOVER_SUCCESS':
      return 'success';
    case 'failed':
    case 'FAILURE':
      return 'failure';
    case 'running':
    case 'PENDING':
      return 'info';
    case 'pending':
      return 'warning';
    default:
      return 'gray';
  }
}

export function getConfidenceColor(level: string): 'success' | 'warning' | 'failure' | 'gray' {
  switch (level) {
    case 'HIGH':
      return 'success';
    case 'MEDIUM':
      return 'warning';
    case 'LOW':
      return 'failure';
    default:
      return 'gray';
  }
}
